import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { API } from "../config";

const AcceptOffer = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [totalSize, setTotalSize] = useState(0);

  const [formData, setFormData] = useState({
    businessLegalName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [documents, setDocuments] = useState({
    driversLicense: null,
    voidedCheck: null,
  });

  const [otherFiles, setOtherFiles] = useState([]);
  const otherFilesRef = useRef(null);

  const MAX_SIZE = 500 * 1024 * 1024; // 500MB — files go direct to S3
  const acceptedTypes = ".pdf,.png,.jpg,.jpeg";
  const acceptedTypesAll = ".pdf,.png,.jpg,.jpeg,.csv,.xls,.xlsx,.doc,.docx";

  const calculateTotalSize = (docs, others) => {
    const docsSize = Object.values(docs).reduce(
      (total, file) => total + (file ? file.size : 0),
      0
    );
    const othersSize = others.reduce((total, file) => total + file.size, 0);
    return docsSize + othersSize;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDocChange = (docType, e) => {
    const file = e.target.files[0] || null;
    const newDocs = { ...documents, [docType]: file };
    setDocuments(newDocs);
    setTotalSize(calculateTotalSize(newDocs, otherFiles));
  };

  const removeDoc = (docType) => {
    const newDocs = { ...documents, [docType]: null };
    setDocuments(newDocs);
    setTotalSize(calculateTotalSize(newDocs, otherFiles));
  };

  const handleOtherFilesChange = (e) => {
    const files = Array.from(e.target.files);
    const newOtherFiles = [...otherFiles, ...files];
    setOtherFiles(newOtherFiles);
    setTotalSize(calculateTotalSize(documents, newOtherFiles));
  };

  const removeOtherFile = (index) => {
    const newOtherFiles = otherFiles.filter((_, i) => i !== index);
    setOtherFiles(newOtherFiles);
    setTotalSize(calculateTotalSize(documents, newOtherFiles));
  };

  const getContentType = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    const types = {
      pdf: "application/pdf",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      csv: "text/csv",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    };
    return types[ext] || "application/octet-stream";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!documents.driversLicense || !documents.voidedCheck) {
      setError("Please upload both your driver's license and a voided check.");
      return;
    }

    if (totalSize > MAX_SIZE) {
      setError("Total file size exceeds 500MB. Please remove some files.");
      return;
    }

    setIsSubmitting(true);

    try {
      // ── Build file list for S3 ──
      const allFiles = [];

      if (documents.driversLicense) {
        allFiles.push({
          file: documents.driversLicense,
          filename: `DriversLicense-${documents.driversLicense.name}`,
        });
      }

      if (documents.voidedCheck) {
        allFiles.push({
          file: documents.voidedCheck,
          filename: `VoidedCheck-${documents.voidedCheck.name}`,
        });
      }

      for (const file of otherFiles) {
        allFiles.push({ file, filename: file.name });
      }

      // ── Step 1: Get presigned upload URLs from S3 ──
      const urlResponse = await fetch(
        API.GET_UPLOAD_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            files: allFiles.map((f) => ({
              filename: f.filename,
              contentType: getContentType(f.filename),
            })),
          }),
        }
      );

      if (!urlResponse.ok) throw new Error("Failed to get upload URLs");
      const { uploadUrls } = await urlResponse.json();

      // ── Step 2: Upload files directly to S3 in parallel ──
      await Promise.all(
        uploadUrls.map((urlInfo, index) =>
          fetch(urlInfo.uploadUrl, {
            method: "PUT",
            headers: {
              "Content-Type": getContentType(allFiles[index].filename),
            },
            body: allFiles[index].file,
          })
        )
      );

      // ── Step 3: Notify backend with S3 keys ──
      const payload = {
        ...formData,
        subject: "Contract Request",
        attachments: uploadUrls.map((urlInfo) => ({
          filename: urlInfo.filename,
          key: urlInfo.key,
        })),
      };

      const response = await fetch(
        API.UPLOAD_DOCUMENTS,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to upload documents");

      navigate("/thank-you");
    } catch (err) {
      setError("Something went wrong. Please try again.");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses =
    "block w-full rounded-xl border border-slate-300 px-4 py-3 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 transition-colors";

  const labelClasses = "block text-sm font-medium text-slate-700 mb-1";

  const docConfig = [
    { key: "driversLicense", label: "Driver's License" },
    { key: "voidedCheck", label: "Voided Check" },
  ];

  return (
    <>
      <SEO
        title="Accept Your Offer"
        description="You're one step away from funding. Upload your driver's license and voided check to request your agreement with Lloyd Funding."
        canonical="/accept-offer"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              Accept Your <span className="text-blue-700">Offer</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              You're one step away from funding. Upload the documents below to
              request your agreement.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="bg-white pb-16">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Card 1: Contact Information */}
            <div className="bg-slate-50 rounded-2xl p-6 ring-1 ring-slate-100">
              <h2 className="text-xl font-semibold text-slate-800 mb-6">
                Contact Information
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="businessLegalName" className={labelClasses}>
                    Business Legal Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="businessLegalName"
                    name="businessLegalName"
                    required
                    value={formData.businessLegalName}
                    onChange={handleInputChange}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="firstName" className={labelClasses}>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className={labelClasses}>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="email" className={labelClasses}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className={labelClasses}>
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={inputClasses}
                  />
                </div>
              </div>
            </div>

            {/* Card 2: Documents */}
            <div className="bg-slate-50 rounded-2xl p-6 ring-1 ring-slate-100">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">
                Required Documents
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Both documents are required to process your funding.
              </p>

              {/* 2 side-by-side upload boxes */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {docConfig.map(({ key, label }) => (
                  <div key={key}>
                    {documents[key] ? (
                      <div className="flex flex-col items-center justify-between rounded-xl border border-slate-300 bg-white p-3 h-24">
                        <span className="text-xs text-slate-600 truncate max-w-full text-center">
                          {documents[key].name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeDoc(key)}
                          className="text-red-500 hover:text-red-700 mt-2"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className="w-5 h-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18 18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white h-24 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-6 h-6 text-slate-400"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                          />
                        </svg>
                        <span className="mt-1 text-xs text-slate-500 text-center px-1">
                          {label}
                        </span>
                        <input
                          type="file"
                          accept={acceptedTypes}
                          onChange={(e) => handleDocChange(key, e)}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                ))}
              </div>

              {/* Additional documents upload */}
              <label className="flex items-center justify-center w-full rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 py-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-8 h-8 mx-auto text-slate-400"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                    />
                  </svg>
                  <span className="mt-2 block text-sm text-slate-500">
                    Click to upload additional documents
                  </span>
                  <span className="mt-1 block text-xs text-slate-400">
                    PDF, PNG, JPG, CSV, Excel, Word
                  </span>
                </div>
                <input
                  type="file"
                  ref={otherFilesRef}
                  accept={acceptedTypesAll}
                  multiple
                  onChange={handleOtherFilesChange}
                  className="hidden"
                />
              </label>

              {/* Additional files list */}
              {otherFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {otherFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-3"
                    >
                      <div className="flex items-center gap-3">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-5 h-5 text-slate-400"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                          />
                        </svg>
                        <div>
                          <p className="text-sm text-slate-600 truncate max-w-[200px] sm:max-w-[300px]">
                            {file.name}
                          </p>
                          <p className="text-xs text-slate-400">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeOtherFile(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-5 h-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18 18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* File Size Indicator */}
            {totalSize > 0 && (
              <div
                className={`rounded-xl p-4 ${
                  totalSize > MAX_SIZE
                    ? "bg-red-50 text-red-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                <div className="flex items-center gap-2">
                  {totalSize > MAX_SIZE && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                      />
                    </svg>
                  )}
                  <span className="text-sm font-medium">
                    Total file size: {formatFileSize(totalSize)} / 500 MB
                    {totalSize > MAX_SIZE &&
                      " — Please remove some files to continue"}
                  </span>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-xl bg-red-50 p-4 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || totalSize > MAX_SIZE}
              className="w-full rounded-xl bg-blue-600 px-8 py-4 text-base font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                "Submit & Accept Offer"
              )}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default AcceptOffer;